/**
 * Auth Service - Frontend
 * @description Service for authentication-related API operations
 */

import { api } from '../config';
import { ApiResponse, AuthResponse } from '@/types';
import { handleApiError } from '../utils/apiUtils';

export interface LoginCredentials {
  username: string;
  password: string;
  /** IANA timezone from browser (saved with last login on server) */
  clientTimezone?: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordReset {
  token: string;
  newPassword: string;
}

export interface ConfirmAccount {
  token: string;
}

export interface SetPasswordWithConfirmation {
  token: string;
  newPassword: string;
}

export interface ValidateConfirmationToken {
  token: string;
}

export interface RefreshTokenRequest {
  token: string;
}

export const authService = {
  // Login user
  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    try {
      const response = await api.post<ApiResponse<AuthResponse>>('/auth/login', credentials);

      if (response.data.success && response.data.data?.token) {
        localStorage.setItem('token', response.data.data.token);
        if (response.data.data.refreshToken) {
          localStorage.setItem('refreshToken', response.data.data.refreshToken);
        }
        localStorage.setItem('user', JSON.stringify(response.data.data.user));
      }

      return response.data;
    } catch (error) {
      console.error('Login error:', error);

      // This is a network/HTTP error
      return {
        success: false,
        error: {
          code: 'LOGIN_FAILED',
          message: handleApiError(error),
        },
      };
    }
  },

  // Refresh token
  async refreshToken(token: string): Promise<ApiResponse<AuthResponse>> {
    try {
      const response = await api.post<ApiResponse<AuthResponse>>('/auth/refresh-token', { token });

      if (response.data.success && response.data.data?.token) {
        localStorage.setItem('token', response.data.data.token);
        if (response.data.data.refreshToken) {
          localStorage.setItem('refreshToken', response.data.data.refreshToken);
        }
        localStorage.setItem('user', JSON.stringify(response.data.data.user));
      }

      return response.data;
    } catch (error) {
      console.error('Refresh token error:', error);

      return {
        success: false,
        error: {
          code: 'REFRESH_TOKEN_FAILED',
          message: handleApiError(error),
        },
      };
    }
  },

  // Logout user
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clean localStorage, even if there's an error
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  // Request password reset
  async requestPasswordReset(
    data: PasswordResetRequest
  ): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await api.post<ApiResponse<{ message: string }>>(
        '/auth/request-password-reset',
        data
      );
      return response.data;
    } catch (error) {
      console.error('Request password reset error:', error);
      return {
        success: false,
        error: {
          code: 'REQUEST_PASSWORD_RESET_FAILED',
          message: handleApiError(error),
        },
      };
    }
  },

  // Reset password
  async resetPassword(data: PasswordReset): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await api.post<ApiResponse<{ message: string }>>(
        '/auth/reset-password',
        data
      );
      return response.data;
    } catch (error) {
      console.error('Reset password error:', error);
      return {
        success: false,
        error: {
          code: 'RESET_PASSWORD_FAILED',
          message: handleApiError(error),
        },
      };
    }
  },

  // Confirm account
  async confirmAccount(data: ConfirmAccount): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await api.post<ApiResponse<{ message: string }>>(
        '/auth/confirm-account',
        data
      );
      return response.data;
    } catch (error) {
      console.error('Confirm account error:', error);
      return {
        success: false,
        error: {
          code: 'CONFIRM_ACCOUNT_FAILED',
          message: handleApiError(error),
        },
      };
    }
  },

  // Set password with confirmation
  async setPasswordWithConfirmation(
    data: SetPasswordWithConfirmation
  ): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await api.post<ApiResponse<{ message: string }>>(
        '/auth/set-password-with-confirmation',
        data
      );
      return response.data;
    } catch (error) {
      console.error('Set password with confirmation error:', error);
      return {
        success: false,
        error: {
          code: 'SET_PASSWORD_FAILED',
          message: handleApiError(error),
        },
      };
    }
  },

  // Validate confirmation token
  async validateConfirmationToken(
    data: ValidateConfirmationToken
  ): Promise<ApiResponse<{ status: string; requiresPasswordSetup?: boolean }>> {
    try {
      const response = await api.post<
        ApiResponse<{ status: string; requiresPasswordSetup?: boolean }>
      >('/auth/validate-confirmation-token', data);
      return response.data;
    } catch (error) {
      console.error('Validate confirmation token error:', error);
      return {
        success: false,
        error: {
          code: 'VALIDATE_TOKEN_FAILED',
          message: handleApiError(error),
        },
      };
    }
  },
};
